import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Users, AlertTriangle, Clock, Snowflake, Activity, Briefcase, ChevronRight,
  ExternalLink, Mail, Phone, Linkedin, HelpCircle, TrendingUp, X,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  LineChart, Line, Legend,
} from 'recharts';
import MetricInfo, { METRIC_INFO_TEXTS } from './MetricInfo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─── Palettes ────────────────────────────────────────────────────────────────
// STATUS palette (semaphore) — reserved exclusively for compliance state
const STATUS = {
  ok: '#10b981',      // green
  warning: '#f59e0b', // amber
  critical: '#ef4444', // red
  unassigned: '#ef4444', // "Sin asignar" also in red per spec
};
const STATUS_STYLES = {
  ok:       { dot: 'bg-emerald-500', ring: 'hover:ring-emerald-300', text: 'text-emerald-700', label: 'OK' },
  warning:  { dot: 'bg-amber-500',   ring: 'hover:ring-amber-300',   text: 'text-amber-700',   label: 'Atención' },
  critical: { dot: 'bg-red-500',     ring: 'hover:ring-red-300',     text: 'text-red-700',     label: 'Crítico' },
};

const HEALTH_ICONS = {
  overdue: AlertTriangle,
  no_cadence: Clock,
  active_90d: Activity,
  decision_makers: Briefcase,
  cooling_accounts: Snowflake,
};

// ─── Health card ─────────────────────────────────────────────────────────────
const HealthCard = ({ item, periodApplies, onOpen, denom }) => {
  const style = item.status ? (STATUS_STYLES[item.status] || null) : null;
  const Icon = HEALTH_ICONS[item.key] || Activity;
  const kind = periodApplies ? 'Flujo' : 'Stock';
  const infoFn = METRIC_INFO_TEXTS[item.key];
  const infoText = typeof infoFn === 'function' ? infoFn(denom) : infoFn;
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      className={`text-left w-full bg-card border rounded-lg p-4 transition-all hover:shadow-md ring-1 ring-transparent ${style?.ring || 'hover:ring-slate-300'} group`}
      data-testid={`health-card-${item.key}`}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-1.5">
          <Icon className={`w-4 h-4 ${style?.text || 'text-slate-600'}`} />
          <span className="text-xs font-medium text-muted-foreground">{item.label}</span>
          {infoText && (
            <MetricInfo
              text={infoText}
              testId={`info-${item.key}`}
              ariaLabel={`Información sobre ${item.label}`}
            />
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <TooltipBadge label={kind} tooltipText={kind === 'Stock' ? METRIC_INFO_TEXTS.stock : METRIC_INFO_TEXTS.flujo} />
          {style && <span className={`w-2 h-2 rounded-full ${style.dot}`} title={style.label} />}
        </div>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-2xl font-bold">{item.value}</span>
        <span className="text-xs text-muted-foreground">de {item.total}</span>
        <span className="text-xs text-muted-foreground">·</span>
        <span className={`text-sm font-medium ${style?.text || 'text-slate-700'}`}>{item.pct}%</span>
      </div>
      {item.key === 'overdue' && item.al_dia !== undefined && (
        <div className="mt-1 text-[11px] text-emerald-700">Al día: <strong>{item.al_dia}</strong></div>
      )}
      <div className="mt-2 h-1.5 w-full bg-muted rounded overflow-hidden">
        <div className={`h-full ${style?.dot || 'bg-slate-400'}`} style={{ width: `${Math.min(item.pct, 100)}%` }} />
      </div>
      <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
        <span className={style?.text || ''}>{style?.label || '\u00A0'}</span>
        <span className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
          Ver lista <ChevronRight className="w-3 h-3" />
        </span>
      </div>
    </button>
  );
};

const TooltipBadge = ({ label, tooltipText }) => (
  <div className="inline-flex items-center gap-0.5">
    <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">{label}</Badge>
    {tooltipText && <MetricInfo text={tooltipText} testId={`info-badge-${label.toLowerCase()}`} ariaLabel={label} />}
  </div>
);

// ─── Cadence pyramid ─────────────────────────────────────────────────────────
const CadencePyramid = ({ cadence, onSegmentClick }) => {
  const rows = cadence.filter(c => c.value > 0);
  const maxTotal = Math.max(1, ...rows.map(r => r.value));
  const segColors = {
    al_dia: STATUS.ok,
    fuera_de_plazo: STATUS.critical,
    sin_fecha: STATUS.warning,
    excluido: '#94a3b8',
    sin_compromiso: '#cbd5e1',
  };
  return (
    <div className="space-y-2.5" data-testid="cadence-pyramid">
      {rows.length === 0 && <div className="text-xs text-muted-foreground">Sin contactos con cadencia asignada</div>}
      {rows.map(row => {
        const width = (row.value / maxTotal) * 100;
        const segs = Object.entries(row.breakdown).filter(([, v]) => v > 0);
        return (
          <div key={row.key} data-testid={`cadence-row-${row.key}`}>
            <div className="flex items-center justify-between text-xs mb-0.5">
              <button
                onClick={() => onSegmentClick(row.key, null)}
                className="flex items-center gap-1.5 hover:underline"
                data-testid={`cadence-bar-${row.key}`}
                title={`Ver lista: ${row.label}`}
              >
                <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: row.color }} />
                <span className="font-medium">{row.label}</span>
              </button>
              <span className="text-muted-foreground">{row.value} <span className="text-[10px]">({row.pct}%)</span></span>
            </div>
            <div className="flex h-5 rounded overflow-hidden bg-muted/40" style={{ width: `${width}%`, minWidth: '20px' }}>
              {segs.map(([sk, sv]) => (
                <button
                  key={sk}
                  type="button"
                  onClick={() => onSegmentClick(row.key, sk)}
                  className="h-full flex items-center justify-center hover:opacity-80"
                  style={{
                    width: `${(sv / row.value) * 100}%`,
                    backgroundColor: segColors[sk] || '#6b7280',
                  }}
                  title={`${row.label} · ${sk.replaceAll('_', ' ')}: ${sv}`}
                  data-testid={`cadence-seg-${row.key}-${sk}`}
                >
                  {sv >= 2 && <span className="text-white text-[10px] font-medium">{sv}</span>}
                </button>
              ))}
            </div>
          </div>
        );
      })}
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] pt-2 border-t">
        {Object.entries(segColors).map(([k, c]) => (
          <span key={k} className="flex items-center gap-1">
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: c }} />
            <span className="text-muted-foreground">{k.replaceAll('_', ' ')}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

const HBarList = ({ items, onClick }) => {
  const max = Math.max(1, ...items.map(i => i.value));
  return (
    <div className="space-y-2">
      {items.map(item => (
        <button
          key={item.key}
          type="button"
          onClick={() => onClick && onClick(item.key)}
          className="w-full text-left group"
          data-testid={`hbar-${item.key}`}
        >
          <div className="flex items-center justify-between text-xs mb-0.5">
            <span className="font-medium group-hover:underline">{item.label}</span>
            <span className="text-muted-foreground">{item.value} <span className="text-[10px]">({item.pct}%)</span></span>
          </div>
          <div className="h-4 bg-muted/40 rounded overflow-hidden">
            <div className="h-full rounded transition-opacity group-hover:opacity-80" style={{ width: `${(item.value / max) * 100}%`, backgroundColor: item.color }} />
          </div>
        </button>
      ))}
    </div>
  );
};

const OwnerHealthBars = ({ items, onSegmentClick }) => {
  const max = Math.max(1, ...items.map(i => i.value));
  return (
    <div className="space-y-2.5" data-testid="owner-health-bars">
      {items.map(o => {
        const alDia = o.breakdown?.al_dia || 0;
        const fuera = o.breakdown?.fuera_de_plazo || 0;
        const otros = o.value - alDia - fuera;
        const width = (o.value / max) * 100;
        const isUnassigned = o.key === 'unassigned';
        return (
          <div key={o.key} data-testid={`owner-row-${o.key}`}>
            <div className="flex items-center justify-between text-xs mb-0.5">
              <button
                onClick={() => onSegmentClick(o.key, null)}
                className={`font-medium truncate max-w-[160px] hover:underline ${isUnassigned ? 'text-red-700' : ''}`}
                data-testid={`owner-bar-${o.key}`}
              >
                {o.label}
              </button>
              <span className="text-muted-foreground">
                {alDia > 0 && <span className="text-emerald-700 mr-2">Al día {alDia}</span>}
                {fuera > 0 && <span className="text-red-700 mr-2">Fuera {fuera}</span>}
                <span>{o.value}</span>
              </span>
            </div>
            <div
              className={`flex h-5 rounded overflow-hidden ${isUnassigned ? 'bg-red-100' : 'bg-muted/40'}`}
              style={{ width: `${width}%`, minWidth: '18px' }}
            >
              {isUnassigned ? (
                <button
                  onClick={() => onSegmentClick(o.key, null)}
                  className="h-full w-full"
                  style={{ backgroundColor: STATUS.unassigned }}
                />
              ) : (
                <>
                  {alDia > 0 && <button onClick={() => onSegmentClick(o.key, 'al_dia')} className="h-full" style={{ width: `${(alDia / o.value) * 100}%`, backgroundColor: STATUS.ok }} data-testid={`owner-seg-${o.key}-al-dia`} />}
                  {fuera > 0 && <button onClick={() => onSegmentClick(o.key, 'fuera_de_plazo')} className="h-full" style={{ width: `${(fuera / o.value) * 100}%`, backgroundColor: STATUS.critical }} data-testid={`owner-seg-${o.key}-fuera`} />}
                  {otros > 0 && <button onClick={() => onSegmentClick(o.key, null)} className="h-full bg-muted-foreground/40" style={{ width: `${(otros / o.value) * 100}%` }} />}
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const CoverageBars = ({ coverage, onSegmentClick }) => (
  <div className="space-y-3" data-testid="coverage-bars">
    {[coverage.companies, coverage.typsa].map(c => (
      <div key={c.key}>
        <div className="flex items-center justify-between text-xs mb-0.5">
          <span className="font-medium">{c.label}</span>
          <span className="text-muted-foreground">
            {c.value} / {c.breakdown.total} · <span className="font-medium">{c.pct}%</span>
          </span>
        </div>
        <div className="h-3 bg-muted/40 rounded overflow-hidden">
          <div className="h-full rounded" style={{ width: `${c.pct}%`, backgroundColor: c.color }} />
        </div>
        <div className="text-[10px] text-muted-foreground mt-0.5">Sin contactos: <strong>{c.breakdown.without_contacts}</strong></div>
      </div>
    ))}
    <div className="pt-2 border-t">
      <div className="text-[11px] font-medium text-muted-foreground mb-1.5">Por estado de la empresa</div>
      <div className="space-y-1.5">
        {coverage.by_status.filter(s => s.breakdown.total > 0).map(s => (
          <button
            key={s.key}
            type="button"
            onClick={() => onSegmentClick && onSegmentClick(s.key)}
            className="w-full text-left"
            data-testid={`coverage-status-${s.key}`}
          >
            <div className="flex items-center justify-between text-xs mb-0.5">
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: s.color }} />
                {s.label}
              </span>
              <span className="text-muted-foreground">
                {s.breakdown.with_contacts} / {s.breakdown.total}
              </span>
            </div>
            <div className="h-2 bg-muted/40 rounded overflow-hidden flex">
              <div className="h-full" style={{ width: `${s.pct}%`, backgroundColor: s.color }} />
              <div className="h-full bg-muted-foreground/20" style={{ width: `${100 - s.pct}%` }} />
            </div>
          </button>
        ))}
      </div>
    </div>
  </div>
);

const ActionTable = ({ rows, mode, onOpenContact }) => {
  if (!rows || rows.length === 0) {
    const emptyText = mode === 'overdue' ? 'Todos los contactos con cadencia están al día 👌'
      : mode === 'no_cadence' ? 'Todos los contactos tienen cadencia asignada'
      : 'Todos los contactos tienen fecha de contacto';
    return <div className="p-6 text-center text-sm text-muted-foreground">{emptyText}</div>;
  }
  return (
    <div className="overflow-x-auto" data-testid={`action-table-${mode}`}>
      <table className="w-full text-sm">
        <thead className="text-xs text-muted-foreground border-b bg-muted/30">
          <tr>
            <th className="text-left px-3 py-2">Contacto</th>
            <th className="text-left px-3 py-2">Cargo</th>
            <th className="text-left px-3 py-2">Empresa</th>
            <th className="text-left px-3 py-2">Responsable</th>
            <th className="text-left px-3 py-2">Cadencia</th>
            <th className="text-right px-3 py-2">Días desde</th>
            {mode === 'overdue' && <th className="text-right px-3 py-2">Exceso</th>}
            <th className="text-right px-3 py-2 w-16">Acción</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.contact_id} className="border-b last:border-0 hover:bg-muted/40" data-testid={`action-row-${r.contact_id}`}>
              <td className="px-3 py-2 font-medium">{r.name}</td>
              <td className="px-3 py-2 text-muted-foreground">{r.role}</td>
              <td className="px-3 py-2 text-muted-foreground">{r.company_name}</td>
              <td className={`px-3 py-2 ${r.owner === 'Sin asignar' ? 'text-red-700 font-medium' : 'text-muted-foreground'}`}>{r.owner}</td>
              <td className="px-3 py-2 text-muted-foreground">
                {r.cadence_label}{r.cadence_limit_days ? ` (${r.cadence_limit_days}d)` : ''}
              </td>
              <td className="px-3 py-2 text-right">{r.never_contacted ? <span className="text-muted-foreground italic">nunca</span> : (r.days_since_last_contact ?? '—')}</td>
              {mode === 'overdue' && <td className="px-3 py-2 text-right"><span className="text-red-700 font-semibold">+{r.days_overdue}d</span></td>}
              <td className="px-3 py-2 text-right">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2"
                  onClick={() => onOpenContact(r.contact_id, mode)}
                  data-testid={`open-contact-${r.contact_id}`}
                  title="Abrir ficha del contacto"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─── Recency mapping to URL param ────────────────────────────────────────────
const RECENCY_KEY_TO_URL = {
  '0_30': '0-30',
  '31_90': '31-90',
  '91_180': '91-180',
  '181_365': '181-365',
  'gt_365': 'gt-365',
  'nunca_contactado': 'nunca',
};

// ─── Metric definitions panel ────────────────────────────────────────────────
const DefinitionsPanel = () => (
  <Sheet>
    <SheetTrigger asChild>
      <Button variant="link" size="sm" className="text-xs h-7 px-2" data-testid="definitions-link">
        <HelpCircle className="w-3.5 h-3.5 mr-1" />
        ¿Cómo se calculan estas métricas?
      </Button>
    </SheetTrigger>
    <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="definitions-panel">
      <SheetHeader>
        <SheetTitle>Definiciones</SheetTitle>
      </SheetHeader>
      <div className="mt-4 space-y-4 text-sm">
        {[
          ['Fuera de plazo', METRIC_INFO_TEXTS.overdue()],
          ['Sin cadencia definida', METRIC_INFO_TEXTS.no_cadence()],
          ['Contactados últimos 90 días', METRIC_INFO_TEXTS.active_90d],
          ['Nivel dirección', METRIC_INFO_TEXTS.decision_makers],
          ['Cuentas enfriándose', METRIC_INFO_TEXTS.cooling_accounts],
          ['Calidad de dato', METRIC_INFO_TEXTS.data_quality],
          ['Pirámide de cadencia', METRIC_INFO_TEXTS.cadence],
          ['Nivel de interlocución', METRIC_INFO_TEXTS.seniority],
          ['Antigüedad del último contacto', METRIC_INFO_TEXTS.recency],
          ['Carga y salud por responsable', METRIC_INFO_TEXTS.owner_health],
          ['Cobertura de cartera', METRIC_INFO_TEXTS.coverage],
          ['Requieren acción', METRIC_INFO_TEXTS.action_list],
          ['Etiqueta Stock', METRIC_INFO_TEXTS.stock],
          ['Etiqueta Flujo', METRIC_INFO_TEXTS.flujo],
        ].map(([title, text]) => (
          <div key={title}>
            <div className="font-semibold text-sm mb-0.5">{title}</div>
            <p className="text-xs text-muted-foreground leading-relaxed">{text}</p>
          </div>
        ))}
      </div>
    </SheetContent>
  </Sheet>
);

// ─── Main ────────────────────────────────────────────────────────────────────
const ContactsAnalytics = ({ clientTypeFilter = ['typsa', 'privada', 'publica'] }) => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('year');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [actionTab, setActionTab] = useState('overdue');

  useEffect(() => {
    fetchAnalytics();
  }, [clientTypeFilter, period, ownerFilter]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('crm_token');
      const params = new URLSearchParams();
      params.append('period', period);
      (clientTypeFilter || []).forEach(t => params.append('company_types', t));
      if (ownerFilter) params.append('owner', ownerFilter);
      const { data } = await axios.get(`${API}/analytics/contacts?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAnalytics(data);
    } catch (err) {
      console.error('Error fetching contact analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const owners = useMemo(() => {
    if (!analytics?.by_owner) return [];
    return analytics.by_owner.filter(o => o.key !== 'unassigned').map(o => o.key);
  }, [analytics]);

  // Build URL for list navigation. Pushes an intermediate entry so browser back returns to Analíticas.
  const navigateToList = (params) => {
    const q = new URLSearchParams();
    (clientTypeFilter || []).forEach(t => q.append('company_types', t));
    Object.entries(params || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, v);
    });
    // Mark current analytics view in URL so back preserves it
    const analyticsUrl = new URLSearchParams(window.location.search);
    analyticsUrl.set('view', 'analytics');
    window.history.replaceState(null, '', `/contacts?${analyticsUrl.toString()}`);
    navigate(`/contacts?${q.toString()}`);
  };

  const handleOpenHealth = (item) => {
    if (item.key === 'cooling_accounts') {
      // Companies list, not contacts
      const q = new URLSearchParams();
      q.set('cooling', 'true');
      navigate(`/companies?${q.toString()}`);
      return;
    }
    const map = {
      overdue: { compliance: 'fuera_de_plazo' },
      no_cadence: { cadence: 'sin_definir' },
      active_90d: { recency: '0-30,31-90' },
      decision_makers: { seniority: 'direccion' },
    };
    navigateToList(map[item.key] || {});
  };

  const handleOpenContact = (contactId, mode) => {
    const extra = {};
    if (mode === 'overdue') extra.compliance = 'fuera_de_plazo';
    if (mode === 'no_cadence') extra.cadence = 'sin_definir';
    if (mode === 'no_date') extra.compliance = 'sin_fecha';
    extra.contactId = contactId;
    navigateToList(extra);
  };

  const handleCadenceClick = (cadenceKey, segKey) => {
    const params = { cadence: cadenceKey };
    if (segKey) params.compliance = segKey;
    navigateToList(params);
  };

  const handleSeniorityClick = (key) => navigateToList({ seniority: key });
  const handleRecencyClick = (key) => navigateToList({ recency: RECENCY_KEY_TO_URL[key] });
  const handleOwnerBarClick = (ownerKey, segKey) => {
    const params = ownerKey === 'unassigned' ? { owner: '__unassigned__' } : { owner: ownerKey };
    if (segKey) params.compliance = segKey;
    navigateToList(params);
  };

  if (loading && !analytics) return <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">Cargando analíticas…</div>;
  if (!analytics) return <div className="text-center py-12 text-muted-foreground">No hay datos disponibles</div>;

  const { health, cadence, seniority, recency, by_owner, coverage, action_list, no_cadence_list, no_contact_date_list, data_quality, period_applies, total, flow } = analytics;
  const anyPeriodApplies = Object.values(period_applies || {}).some(v => v === true);
  const flowRepresented = flow?.represented ?? 0;
  const flowScope = flow?.total_scope ?? total;

  const overdueDenom = health?.overdue?.total;
  const noCadenceDenom = health?.no_cadence?.total;

  return (
    <div className="space-y-6" data-testid="contacts-analytics">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Users className="w-5 h-5" /> Analítica de Contactos
          </h2>
          <p className="text-xs text-muted-foreground">
            <strong>{total}</strong> contactos en el ámbito · Calidad de dato:{' '}
            <span className="inline-flex items-center gap-1" data-testid="dq-email"><Mail className="w-3 h-3" /> {data_quality?.email?.pct ?? 0}%</span>
            <span className="mx-1.5">·</span>
            <span className="inline-flex items-center gap-1" data-testid="dq-phone"><Phone className="w-3 h-3" /> {data_quality?.phone?.pct ?? 0}%</span>
            <span className="mx-1.5">·</span>
            <span className="inline-flex items-center gap-1" data-testid="dq-linkedin"><Linkedin className="w-3 h-3" /> {data_quality?.linkedin?.pct ?? 0}%</span>
            <MetricInfo text={METRIC_INFO_TEXTS.data_quality} testId="info-data-quality" ariaLabel="Calidad de dato" />
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DefinitionsPanel />
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="period-select">
              <SelectValue placeholder="Periodo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todo el histórico</SelectItem>
              <SelectItem value="year">Últ. 12 meses</SelectItem>
              <SelectItem value="quarter">Últ. 3 meses</SelectItem>
              <SelectItem value="month">Últ. mes</SelectItem>
            </SelectContent>
          </Select>
          {!anyPeriodApplies && (
            <Badge variant="outline" className="text-[10px]" data-testid="cartera-completa-tag">Cartera completa · el periodo no aplica</Badge>
          )}
          <Select value={ownerFilter || 'all'} onValueChange={(v) => setOwnerFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[190px] h-8 text-xs" data-testid="owner-select">
              <SelectValue placeholder="Responsable" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los responsables</SelectItem>
              {owners.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* BLOCK 1 */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="block-health">
        {Object.values(health).map(h => (
          <HealthCard
            key={h.key}
            item={h}
            periodApplies={period_applies?.[h.key] === true}
            onOpen={handleOpenHealth}
            denom={h.key === 'overdue' ? overdueDenom : h.key === 'no_cadence' ? noCadenceDenom : null}
          />
        ))}
      </div>

      {/* BLOCK 2 */}
      <div className="grid md:grid-cols-2 gap-5" data-testid="block-charts">
        <Card data-testid="chart-cadence">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              Pirámide de cadencia
              <MetricInfo text={METRIC_INFO_TEXTS.cadence} testId="info-cadence" ariaLabel="Pirámide de cadencia" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <CadencePyramid cadence={cadence} onSegmentClick={handleCadenceClick} />
          </CardContent>
        </Card>

        <Card data-testid="chart-seniority">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              Nivel de interlocución
              <MetricInfo text={METRIC_INFO_TEXTS.seniority} testId="info-seniority" ariaLabel="Nivel de interlocución" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <HBarList items={seniority} onClick={handleSeniorityClick} />
          </CardContent>
        </Card>

        <Card data-testid="chart-recency">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              Antigüedad del último contacto
              <MetricInfo text={METRIC_INFO_TEXTS.recency} testId="info-recency" ariaLabel="Antigüedad del último contacto" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={recency} onClick={(e) => e?.activePayload?.[0]?.payload?.key && handleRecencyClick(e.activePayload[0].payload.key)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={60} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]} cursor="pointer">
                    {recency.map(r => <Cell key={r.key} fill={r.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="chart-owner-health">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              Carga y salud por responsable
              <MetricInfo text={METRIC_INFO_TEXTS.owner_health} testId="info-owner-health" ariaLabel="Carga y salud por responsable" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-[240px] overflow-y-auto pr-1">
              <OwnerHealthBars items={by_owner} onSegmentClick={handleOwnerBarClick} />
            </div>
          </CardContent>
        </Card>

        <Card className="md:col-span-2" data-testid="chart-coverage">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              Cobertura de cartera
              <MetricInfo text={METRIC_INFO_TEXTS.coverage} testId="info-coverage" ariaLabel="Cobertura de cartera" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <CoverageBars coverage={coverage} />
          </CardContent>
        </Card>
      </div>

      {/* BLOCK: FLOW (period_applies=true) */}
      {flow && (
        <Card data-testid="block-flow">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-1.5">
              <TrendingUp className="w-4 h-4" />
              Actividad en el periodo
              <TooltipBadge label="Flujo" tooltipText={METRIC_INFO_TEXTS.flujo} />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={flow.series}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line
                    type="monotone"
                    dataKey="new_contacts"
                    stroke="#3b82f6"
                    name="Altas nuevas"
                    dot={(props) => {
                      const { payload, cx, cy } = props;
                      return <circle cx={cx} cy={cy} r={payload.partial ? 5 : 3} fill={payload.partial ? '#fff' : '#3b82f6'} stroke="#3b82f6" strokeWidth={payload.partial ? 2 : 1} strokeDasharray={payload.partial ? '2 2' : ''} />;
                    }}
                    strokeDasharray=""
                  />
                  <Line
                    type="monotone"
                    dataKey="interacted_contacts"
                    stroke="#10b981"
                    name="Contactos con interacción"
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
              <span>Mes en curso con línea discontinua · <em>parcial</em></span>
              <span>Incluye {flowRepresented} de {flowScope} contactos</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* BLOCK 3 */}
      <Card data-testid="block-action">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            Requieren acción
            <MetricInfo text={METRIC_INFO_TEXTS.action_list} testId="info-action-list" ariaLabel="Requieren acción" />
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Tabs value={actionTab} onValueChange={setActionTab}>
            <TabsList className="mx-3 mt-3" data-testid="action-tabs">
              <TabsTrigger value="overdue" data-testid="tab-overdue">Fuera de plazo ({action_list.length})</TabsTrigger>
              <TabsTrigger value="no_cadence" data-testid="tab-no-cadence">Sin cadencia asignada ({no_cadence_list.length})</TabsTrigger>
              <TabsTrigger value="no_date" data-testid="tab-no-date">Sin fecha de contacto registrada ({no_contact_date_list.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="overdue" className="mt-0">
              <ActionTable rows={action_list} mode="overdue" onOpenContact={handleOpenContact} />
            </TabsContent>
            <TabsContent value="no_cadence" className="mt-0">
              <ActionTable rows={no_cadence_list} mode="no_cadence" onOpenContact={handleOpenContact} />
            </TabsContent>
            <TabsContent value="no_date" className="mt-0">
              <ActionTable rows={no_contact_date_list} mode="no_date" onOpenContact={handleOpenContact} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default ContactsAnalytics;
